<script setup lang="ts">
/**
 * Textarea that collapses long content into a faded preview with a "Show more / Show less"
 * control at the bottom of the field. Short content renders as a plain textarea: whether the
 * content is cut off is measured (wrapping depends on the width), not guessed from line counts.
 *
 * - `readonly` locks the content, e.g. for read-only previews.
 * - Otherwise focusing the textarea expands it, so nobody types into a hidden area.
 * - `v-model:expanded` is optional; use it to keep the state per item (e.g. per tab).
 * - Attributes (`aria-invalid`, `placeholder`, listeners, `data-*`) go to the textarea itself,
 *   while `class` styles the bordered wrapper. `aria-invalid` turns the wrapper border red.
 */
import { nextTick, ref, watch, type HTMLAttributes } from "vue"
import { useResizeObserver, useVModel } from "@vueuse/core"
import { ChevronDown, ChevronUp } from "@lucide/vue"
import { useI18n } from "vue-i18n"
import { cn } from "@/lib/utils"
import Textarea from "./Textarea.vue"

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    readonly?: boolean
    /** Classes for the bordered wrapper. */
    class?: HTMLAttributes["class"]
    /** Classes for the textarea itself, e.g. fonts. */
    textareaClass?: HTMLAttributes["class"]
    /** Height limit while collapsed. */
    collapsedClass?: HTMLAttributes["class"]
  }>(),
  {
    collapsedClass: "max-h-64",
  },
)

const emits = defineEmits<{
  (e: "update:modelValue", payload: string | number): void
}>()

const modelValue = useVModel(props, "modelValue", emits, { passive: true })
const expanded = defineModel<boolean>("expanded", { default: false })

const { t } = useI18n()

const wrapperEl = ref<HTMLElement | null>(null)
const textareaRef = ref<InstanceType<typeof Textarea> | null>(null)
const clipped = ref(false)

/**
 * Only measured while collapsed: once expanded the textarea grows to fit and would report no
 * overflow, which would hide the collapse control.
 */
async function measureClipping() {
  if (expanded.value) return
  await nextTick()
  const el = (textareaRef.value as any)?.$el as HTMLTextAreaElement | undefined
  clipped.value = !!el && el.scrollHeight > el.clientHeight + 1
}

watch([modelValue, expanded], measureClipping, { immediate: true })
useResizeObserver(wrapperEl, measureClipping)

function onFocus() {
  if (!props.readonly) expanded.value = true
}
</script>

<template>
  <div
    ref="wrapperEl"
    data-slot="collapsible-textarea"
    :class="cn(
      'overflow-hidden rounded-md border border-input shadow-xs transition-[color,box-shadow] dark:bg-input/30',
      'has-[[aria-invalid=true]]:border-destructive',
      !readonly && 'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 has-[[aria-invalid=true]]:focus-within:ring-destructive/20',
      props.class,
    )"
  >
    <Textarea
      ref="textareaRef"
      v-bind="$attrs"
      v-model="modelValue"
      :readonly="readonly"
      :class="cn(
        'rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent',
        readonly && 'cursor-default resize-none',
        !expanded && [collapsedClass, 'overflow-hidden'],
        !expanded && clipped && 'collapsible-textarea-clipped',
        textareaClass,
      )"
      @focus="onFocus"
    />

    <!-- Expand / collapse control, always at the bottom of the field -->
    <button
      v-if="clipped"
      type="button"
      class="flex w-full cursor-pointer items-center justify-center gap-1.5 border-t bg-muted/40 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <component :is="expanded ? ChevronUp : ChevronDown" class="h-3.5 w-3.5" />
      {{ expanded ? t("common.showLess") : t("common.showMore") }}
    </button>
  </div>
</template>

<style scoped>
/* Collapsed preview: fade the text itself out towards the bottom edge instead of laying a
   coloured gradient over it, so it holds on any surface and in both themes. Applied to the
   textarea only, so the wrapper's border stays crisp. */
.collapsible-textarea-clipped {
  -webkit-mask-image: linear-gradient(to bottom, #000 60%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 60%, transparent 100%);
}
</style>
